"use client";

import { AVATAR_COLORS, DEFAULT_AVATAR } from "@constants/global";
import Avatar from "boring-avatars";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import moment from "moment";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import React from "react";
import ReactTimeAgo from "react-time-ago";
import { IUser } from "types/User.types";
import { UserTooltip } from "./user-tooltip";

export interface PostAuthorProps {
  profileImage?: string | null;
  name: string;
  createdAt?: Date | string | null;
  edited?: boolean;
  avatarSize?: number;
  userId?: string | null;
  user?: Partial<IUser> | null;
}

export function PostAuthor({
  profileImage,
  name,
  createdAt,
  edited,
  avatarSize = 32,
  userId,
  user,
}: PostAuthorProps) {
  const { lang, t } = useTranslation("common");

  const avatar =
    profileImage === DEFAULT_AVATAR || !profileImage ? (
      <Avatar size={avatarSize} name={name} variant="beam" colors={AVATAR_COLORS} />
    ) : (
      <AvatarWithInitials urlImg={profileImage} initials={name} customSize={avatarSize} />
    );

  const resolvedUserId = user?.id ?? userId;
  const tooltipUser = user ?? {
    id: resolvedUserId ?? undefined,
    name,
    profileImage: profileImage ?? undefined,
  };

  const nameClassName =
    "truncate text-[12.5px] font-semibold leading-none text-gray-800 transition-colors hover:text-indigo-600 dark:text-gray-100 dark:hover:text-indigo-300";

  return (
    <UserTooltip user={tooltipUser}>
      <div className="flex min-w-0 items-center gap-2">
        <div className="shrink-0 overflow-hidden rounded-full ring-1 ring-gray-100 dark:ring-gray-800">
          {avatar}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          {resolvedUserId ? (
            <Link
              href={`/profile/${resolvedUserId}`}
              className={nameClassName}
              onClick={(event) => event.stopPropagation()}
            >
              {name}
            </Link>
          ) : (
            <span className={nameClassName}>{name}</span>
          )}
          {createdAt && (
            <span className="text-[10.5px] leading-none text-gray-400 dark:text-gray-500">
              <ReactTimeAgo
                date={moment(createdAt).toDate()}
                locale={lang === "es" ? "es-CL" : "en-US"}
              />
              {edited && <span className="ml-1 italic">· {t("Edited")}</span>}
            </span>
          )}
        </div>
      </div>
    </UserTooltip>
  );
}
